/**
 * 브랜드 인덱스를 화면에 적용한다. 두 가지를 한다.
 *
 *   1. 상품 카드의 브랜드 표기(`[data-brand-vendor]`)를 `display_name` 으로 교체
 *   2. 검색 제안 칩(`[data-brand-keyword]`)의 링크를 브랜드 컬렉션으로 교체
 *
 * 둘 다 서버에서 "맞지만 덜 좋은" 값을 먼저 그려두고 여기서 올려치는 구조다. 인덱스를
 * 못 받아도 카드에는 vendor 가 그대로 남고 칩은 /search 로 가므로 화면은 멀쩡하다
 * (칩은 snippets/search-brand-redirect 가 이어받아 결국 컬렉션에 도착한다).
 *
 * ── 왜 Liquid 에서 못 하나 ──
 * 브랜드가 250개를 넘겨 `{% paginate ... by 250 %}` 로는 전부 볼 수 없고, 상한을 올릴
 * 수도 없다. 핸들 직접 조회는 페이지당 고유 handle 20개가 전역 상한이라 카드 목록에
 * 못 쓴다. 게다가 예전 코드는 브랜드마다 `collections[...]` 을 조회해 컬렉션 존재를
 * 확인했는데, 이 조회에도 페이지 단위 예산이 있어서 앞에서 다 써버리면 뒤쪽 조회가
 * 조용히 빈 값을 돌려줬다 — 같은 브랜드가 지면에 따라 되기도 안 되기도 한 원인이다.
 *
 * ── 왜 스니펫 인라인 스크립트가 아니라 전역 모듈인가 ──
 * 검색 모달의 빈 상태는 `load_empty_state: false` 로 처음엔 안 그려지고, 나중에
 * Section Rendering API 로 받아 DOM 에 주입된다. 주입된 <script> 는 실행되지 않으므로
 * 칩 처리는 전역 모듈 + MutationObserver 로 해야 한다.
 *
 * 상세 페이지(blocks/piyonna-product-info)는 브랜드가 하나뿐이라 Liquid 에서 직접
 * 조회한다. 여기 대상이 아니다.
 */

import { getBrandIndexSync, loadBrandIndex } from '@theme/brand-index';

const VENDOR_SELECTOR = '[data-brand-vendor]';
const KEYWORD_SELECTOR = '[data-brand-keyword]';
const SELECTOR = `${VENDOR_SELECTOR}, ${KEYWORD_SELECTOR}`;

/**
 * @param {Element} element
 * @param {import('./brand-index.js').BrandIndex} index
 */
function applyToElement(element, index) {
  const { brandVendor, brandKeyword } = /** @type {HTMLElement} */ (element).dataset;

  if (brandVendor) {
    const handle = index.handleByKeyword.get(brandVendor.toLowerCase().trim());
    const brand = handle && index.brands.find((entry) => entry.handle === handle);
    // 표시명이 따로 없으면 서버가 그린 vendor 가 이미 정답이다
    if (brand?.label && brand.label !== element.textContent.trim()) {
      element.textContent = brand.label;
    }
    delete (/** @type {HTMLElement} */ (element).dataset.brandVendor);
    return;
  }

  if (brandKeyword) {
    // 인덱스에는 컬렉션이 실제로 있는 브랜드의 URL 만 들어 있어 404 가 날 수 없다.
    const url = index.urlByKeyword.get(brandKeyword.toLowerCase().trim());
    if (url && element instanceof HTMLAnchorElement) element.href = url;
    delete (/** @type {HTMLElement} */ (element).dataset.brandKeyword);
  }
}

/**
 * @param {Node} root
 * @param {import('./brand-index.js').BrandIndex} index
 */
function applyWithin(root, index) {
  if (!(root instanceof Element) && !(root instanceof Document)) return;

  if (root instanceof Element && root.matches(SELECTOR)) applyToElement(root, index);
  for (const element of root.querySelectorAll(SELECTOR)) applyToElement(element, index);
}

/** 지금 화면에 있는 요소에 적용한다. 섹션을 다시 그린 뒤 직접 부를 수도 있다. */
export function applyBrandIndexToDom(root = document) {
  const index = getBrandIndexSync();
  if (index) applyWithin(root, index);
}

const index = getBrandIndexSync() ?? (await loadBrandIndex());

if (index) {
  applyWithin(document, index);

  // 검색 모달의 빈 상태·검색 결과·캐러셀처럼 나중에 붙는 것도 따라간다.
  new MutationObserver((records) => {
    for (const record of records) {
      for (const node of record.addedNodes) applyWithin(node, index);
    }
  }).observe(document.body, { childList: true, subtree: true });
}
